import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { appToasterVars } from '@/components/AppToaster';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { CloseSmallIcon } from '@/ui/Icons/CloseSmallIcon';

import { classNames } from '@/utils/classnames';

import { BackgroundProgressAnimation } from './BackgroundProgressAnimation';

const toastStackHeights = new Map<string, number>();

const updateToastStackHeightProperty = () => {
  const heights = Array.from(toastStackHeights.values());

  if (!heights.length) {
    document.documentElement.style.removeProperty('--toast-stack-height');
    return;
  }

  const toastHeights = heights.reduce((totalHeight, height) => totalHeight + height, 0);

  const bottomOffset = `var(${appToasterVars.bottomOffset})`;
  const toastGaps = `calc(${Math.max(0, heights.length - 1)} * var(${appToasterVars.gap}))`;
  const stackHeight = `calc(${bottomOffset} + ${toastGaps} + ${toastHeights}px)`;

  document.documentElement.style.setProperty('--toast-stack-height', stackHeight);
};

interface ToastContainerProps {
  children: ReactNode;
  onClose?: () => void;
  onHeightChange?: () => void;
  showProgressAnimation?: boolean;
  onHoverChange: (hovered: boolean) => void;
}

/**
 * Shared container for all toast types with common styling and behavior
 */
export function ToastContainer({
  children,
  onClose,
  onHeightChange,
  showProgressAnimation = false,
  onHoverChange,
}: ToastContainerProps) {
  const [startX, setStartX] = useState<number | null>(null);

  const isMobile = useMedia(breakpoints.mobile, false);
  const id = useId();

  const containerRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const height = Math.ceil(container.getBoundingClientRect().height);
      const previousHeight = previousHeightRef.current;
      previousHeightRef.current = height;
      toastStackHeights.set(id, height);
      updateToastStackHeightProperty();

      // Sonner does not observe size changes inside custom toast content.
      if (previousHeight != null && previousHeight !== height) {
        onHeightChange?.();
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      toastStackHeights.delete(id);
      updateToastStackHeightProperty();
    };
  }, [id, isMobile, onHeightChange]);

  const handlePointerDown = (e: React.PointerEvent) => {
    setStartX(e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (startX === null || !onClose) return;
    const deltaX = e.clientX - startX;
    if (deltaX > 100) {
      onClose();
    }
    setStartX(null);
  };

  return (
    <div
      ref={containerRef}
      data-type="toast"
      className="relative w-[calc(100vw-32px)] pointer-events-auto max-w-[388px] shrink-0 rounded-xl overflow-hidden shadow-25 dark:border-1 dark:border-[#4d4e5e]"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      style={{ touchAction: 'pan-y' }}
    >
      <div className="absolute inset-0 rounded-xl border border-token-gray-300 pointer-events-none" />
      <div className="flex flex-row gap-3 relative bg-special-background-modal p-4">
        {/* Background Progress Animation */}
        {showProgressAnimation && <BackgroundProgressAnimation display={true} />}

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className={classNames(
              'text-token-gray-700 size-8 p-[7px] cursor-pointer absolute top-2.5 right-2.5 rounded-[8px] z-20',
              'hover:text-token-gray-800 hover:bg-token-gray-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-token-gray-400 focus-visible:text-token-gray-800',
              'transition-all duration-150 ease-in-out',
            )}
            type="button"
            aria-label="Close notification"
          >
            <CloseSmallIcon className="size-4.5" />
          </button>
        )}

        {children}
      </div>
    </div>
  );
}

interface ToastTitleProps {
  children: ReactNode;
  className?: string;
}

/**
 * Shared title styling for all toasts
 */
export function ToastTitle({ children, className }: ToastTitleProps) {
  const stylesClassName =
    className ??
    fontStyles({
      variant: 'BODY_MD',
      class: ['text-token-gray-800'],
    });
  return <div className={stylesClassName}>{children}</div>;
}

interface ToastContentProps {
  children: ReactNode;
}

/**
 * Shared content container for all toasts
 */
export function ToastContent({ children }: ToastContentProps) {
  return <div className="flex flex-col gap-1 min-w-0 text-sm">{children}</div>;
}

/**
 * Hook to manage auto-dismiss logic with hover pause
 */
export function useAutoDismiss(
  isHovered: boolean,
  onClose: (() => void) | undefined,
  isPending: boolean,
  isCrossChain?: boolean,
) {
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(6000);

  useEffect(() => {
    // Only set up auto-dismiss if onClose callback is provided
    if (!onClose) return;

    // Don't auto-dismiss pending market toasts - they should persist until completion
    if (isPending && !isCrossChain) {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      return;
    }

    // Don't auto-dismiss pending cross-chain toasts - they persist until terminal state
    if (isPending && isCrossChain) {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      return;
    }

    // Initialize start time on first mount
    if (startTimeRef.current === 0) {
      startTimeRef.current = Date.now();
      remainingTimeRef.current = 6000;
    }

    // Clear any existing timer
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }

    if (isHovered) {
      // While hovering, update remaining time
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
      startTimeRef.current = Date.now();
    } else {
      // Not hovering - set timer for remaining time or minimum 2 seconds
      const timeout = Math.max(2000, remainingTimeRef.current);
      dismissTimerRef.current = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, timeout);

      // Update start time for tracking elapsed time during next hover
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now();
      }
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [isHovered, onClose, isPending, isCrossChain]);
}
